/**
 * @file PhoneMindmapTree.tsx
 * @brief The phone's Mind Map tab on the web: a file-tree, not a canvas.
 *
 * @details
 * DES (30-09-2026): "mind maps bhi mobile say match karo". Port of mobile
 * `tabs/MeetingMindmapTab.tsx` + `meetingDetailsStyles.ts` (mindmap*):
 * - A "{n} nodes" chip, then the tree flush on the page (no card).
 * - 28px gutter columns; 1px connectors in the sub-heading grey: a spine per
 *   open ancestor, an elbow into each row that stops 3px short of the icon,
 *   and a drop line under an open node's icon.
 * - Type icon 20 (root 22) in the type colour, label 15/21 (root bold 16),
 *   the type under it (11 semibold, capitalised), a chevron on the right for
 *   branches. Only the root starts open; actions are hoisted beside their
 *   decisions (`hoistMindmapActions`).
 * - Web extra: clicking a leaf opens its evidence card under it (the moment
 *   in the meeting it came from), as the web canvas did.
 *
 * @author DES
 * @date 30-09-2026
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Network } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import type { MeetingDetail } from "../schemas";
import {
  buildMindmapTree,
  hoistMindmapActions,
  mindmapVisual,
  rootExpandedIds,
  type MindmapTreeNode,
} from "./mindmapGraph";
import { buildMindmapEvidenceIndex, resolveMindmapEvidence } from "./mindmapEvidence";
import { MeetingMindmapEvidence } from "./MeetingMindmapEvidence";

/** Mobile textSubHeading: #a0a0a0 light, #737373 dark. */
const LINE = "bg-[#a0a0a0] dark:bg-[#737373]";
/** Icon centre from the row top: 8px padding + half the 20px glyph. */
const ICON_MID = 18;

function Gutter({ children }: { children?: React.ReactNode }) {
  return <div className="relative min-h-11 w-7 shrink-0">{children}</div>;
}

function Row({
  node,
  depth,
  isLast,
  ancestorContinues,
  expanded,
  onToggle,
  selectedId,
  onSelect,
  renderEvidence,
}: {
  node: MindmapTreeNode;
  depth: number;
  isLast: boolean;
  ancestorContinues: boolean[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  renderEvidence: (node: MindmapTreeNode) => React.ReactNode;
}) {
  const hasChildren = node.children.length > 0;
  const open = hasChildren && expanded.has(node.id);
  const { Icon, color } = mindmapVisual(node.type);
  const isRoot = depth === 0;
  const iconSize = isRoot ? 22 : 20;
  const selected = selectedId === node.id;

  return (
    <div>
      <button
        type="button"
        onClick={() => (hasChildren ? onToggle(node.id) : onSelect(selected ? null : node.id))}
        aria-expanded={hasChildren ? open : undefined}
        className={cn(
          "flex w-full items-stretch rounded-xl text-left transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.04]",
          selected && "bg-black/[0.04] dark:bg-white/[0.06]",
        )}
      >
        {Array.from({ length: depth }, (_, i) =>
          i === depth - 1 ? (
            <Gutter key={i}>
              {/* Elbow: down to the icon's middle, on if not the last, across. */}
              <span className={cn("absolute left-[13px] top-0 w-px", LINE)} style={{ height: ICON_MID + 1 }} />
              {!isLast ? (
                <span className={cn("absolute bottom-0 left-[13px] w-px", LINE)} style={{ top: ICON_MID }} />
              ) : null}
              <span className={cn("absolute left-[14px] h-px w-[11px]", LINE)} style={{ top: ICON_MID }} />
            </Gutter>
          ) : (
            <Gutter key={i}>
              {ancestorContinues[i] ? (
                <span className={cn("absolute bottom-0 left-[13px] top-0 w-px", LINE)} />
              ) : null}
            </Gutter>
          ),
        )}

        <div className="relative flex w-7 shrink-0 justify-center py-2">
          {open ? (
            <span className={cn("absolute bottom-0 left-[13px] w-px", LINE)} style={{ top: 8 + iconSize + 3 }} />
          ) : null}
          <Icon style={{ color, width: iconSize, height: iconSize }} strokeWidth={2} />
        </div>

        <div className="ml-2 min-w-0 flex-1 py-2">
          <p
            className={cn(
              "line-clamp-4 text-app-text",
              isRoot ? "text-base font-bold leading-[21px]" : "text-[15px] leading-[21px]",
            )}
          >
            {node.label}
          </p>
          {!isRoot ? (
            <p className="mt-0.5 text-[11px] font-semibold capitalize text-app-text-secondary">{node.type}</p>
          ) : null}
        </div>

        {hasChildren ? (
          open ? (
            <ChevronDown className="ml-2 mr-1 mt-2.5 h-4 w-4 shrink-0 text-app-text-secondary" />
          ) : (
            <ChevronRight className="ml-2 mr-1 mt-2.5 h-4 w-4 shrink-0 text-app-text-secondary" />
          )
        ) : null}
      </button>

      {selected && !hasChildren ? (
        // Inline, no card (DES 30-09-2026). The tree's spines run on through
        // it, so the evidence reads as part of this row, not a break.
        <div className="flex">
          {Array.from({ length: depth }, (_, i) => {
            const continues = i === depth - 1 ? !isLast : ancestorContinues[i];
            return (
              <div key={i} className="relative w-7 shrink-0">
                {continues ? <span className={cn("absolute bottom-0 left-[13px] top-0 w-px", LINE)} /> : null}
              </div>
            );
          })}
          <div className="min-w-0 flex-1 pb-3 pl-9 pr-2 pt-1">{renderEvidence(node)}</div>
        </div>
      ) : null}

      {open
        ? node.children.map((child, i) => (
            <Row
              key={child.id}
              node={child}
              depth={depth + 1}
              isLast={i === node.children.length - 1}
              ancestorContinues={[...ancestorContinues.slice(0, Math.max(0, depth - 1)), !isLast]}
              expanded={expanded}
              onToggle={onToggle}
              selectedId={selectedId}
              onSelect={onSelect}
              renderEvidence={renderEvidence}
            />
          ))
        : null}
    </div>
  );
}

export function PhoneMindmapTree({ meeting }: { meeting: MeetingDetail }) {
  const mindmap = meeting.meeting_mindmap ?? null;
  const nodeCount = mindmap?.nodes?.length ?? 0;
  const tree = useMemo(
    () => (mindmap ? hoistMindmapActions(buildMindmapTree(mindmap)) : []),
    [mindmap],
  );
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    mindmap ? rootExpandedIds(mindmap) : new Set<string>(),
  );
  // A regenerate replaces the map: start again from the root, as the phone does.
  useEffect(() => {
    if (mindmap) setExpanded(rootExpandedIds(mindmap));
  }, [mindmap?.rootId, mindmap?.nodes?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const evidenceIndex = useMemo(() => buildMindmapEvidenceIndex(meeting), [meeting]);
  const renderEvidence = (node: MindmapTreeNode) => (
    <MeetingMindmapEvidence
      node={node}
      evidence={resolveMindmapEvidence(node, evidenceIndex)}
      recordingUrl={meeting.recording_url ?? null}
      onClose={() => setSelectedId(null)}
      bare
    />
  );

  if (!mindmap || !(mindmap.nodes ?? []).some((n) => n.type !== "root")) {
    return (
      <p className="py-12 text-center text-[15px] text-app-text-secondary">
        A mind map is generated once this meeting&apos;s intelligence is compiled.
      </p>
    );
  }

  return (
    <div className="pb-6">
      <div className="mb-3 flex">
        <span className="inline-flex items-center gap-1 rounded-full bg-black/[0.04] px-2 py-1 text-xs font-semibold text-app-text-secondary dark:bg-white/[0.06]">
          <Network className="h-3.5 w-3.5" />
          {nodeCount} nodes
        </span>
      </div>
      {tree.map((root, i) => (
        <Row
          key={root.id}
          node={root}
          depth={0}
          isLast={i === tree.length - 1}
          ancestorContinues={[]}
          expanded={expanded}
          onToggle={toggle}
          selectedId={selectedId}
          onSelect={setSelectedId}
          renderEvidence={renderEvidence}
        />
      ))}
    </div>
  );
}
